import { svgIconClassName } from '@archipro-design/icons';
import {
    pxToRem,
    carouselNavigationClassName,
    carouselPaddleClassName,
    carouselOverlayClassName,
    carouselNavigationItemClassName,
    carouselPaddlesContainerClassName,
} from '@archipro-design/aria';
import type { StyleRule } from '@archipro-design/aria';
import {
    buttonClassName,
    carouselSlotClassNames,
} from '@fluentui/react-northstar';
import { hex2rgba } from '~/utils';

export const PRODUCT_GALLERY_SIZE = 780;
export const PRODUCT_THUMB_SIZE = 123.33;
export const PRODUCT_THUMB_GAP = 8;
/** Shared corner radius for gallery image/video tiles, thumbs, and lightbox media. */
export const PRODUCT_GALLERY_MEDIA_RADIUS_PX = 10;
const IMAGE_INITIAL_SCALE = '1';

export const Carousel: StyleRule<{ swiperInitialised?: boolean }> = ({
    theme,
    swiperInitialised,
}) => ({
    [`& .${carouselOverlayClassName}`]: {
        pointerEvents: 'none',
        backgroundColor: 'transparent',
    },
    '& .swiper-initialised': {
        ['& .' + buttonClassName]: {
            visibility: 'visible',
        },
    },
    [theme.screen.tablet]: {
        display: 'block',
        [`& .${carouselPaddleClassName}`]: {
            height: pxToRem(500),
            borderRadius: 'none',
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: 'transparent',
            },
            '&:focus-visible': {
                '&::before, &::after': {
                    display: 'none',
                },
            },
            ['&.' + carouselSlotClassNames.paddleNext]: {
                borderRadius: 0,
                right: pxToRem(19),
                justifyContent: 'flex-end',
            },
            ['&.' + carouselSlotClassNames.paddlePrevious]: {
                borderRadius: 0,
                left: pxToRem(19),
                justifyContent: 'flex-start',
            },
        },
        ['& .' + carouselSlotClassNames.itemsContainer]: {
            [`& .${carouselOverlayClassName}`]: {
                padding: pxToRem(20),
            },
        },
        ['& .' + carouselPaddlesContainerClassName]: {
            transition: 'background-color 150ms',
            zIndex: theme.siteVariables.zIndexes.overlay,
            pointerEvents: 'none',
        },
        '& .swiper-slide.swiper-slide-active': {
            zIndex: theme.siteVariables.zIndexes.foreground,
        },
        '& .swiper-slide': {
            transform: `scale(${IMAGE_INITIAL_SCALE})`,
            transition: swiperInitialised ? 'transform 150ms' : 'none',
            zIndex: theme.siteVariables.zIndexes.background,
        },
        '& :not(:has(.swiper-slide-active))': {
            '& .swiper-slide:first-child': {
                zIndex: theme.siteVariables.zIndexes.foreground,
            },
        },
        '&:hover': {
            '& .swiper-slide': {
                transform: 'scale(1)',
            },
            ['& .' + carouselPaddlesContainerClassName]: {
                backgroundColor: hex2rgba(
                    theme.siteVariables.colors.primitive.black,
                    0.02,
                ),
            },
        },
    },
});

export const GalleryWrapper: StyleRule = () => ({
    position: 'relative',
    overflow: 'hidden',
    // Gallery holds media only: suppress text selection so repeated taps never tint the tiles.
    userSelect: 'none',
});

/**
 * Mobile gallery slides are content-sized (slidesPerView 'auto'): every tile — image or
 * video — gets the same square footprint as the desktop-era fixed box, capped so a slide
 * always fits the viewport (offsets + peek) instead of overlapping its neighbours.
 */
export const MobileCarouselSlides: StyleRule = () => ({
    '& .swiper-slide': {
        width: pxToRem(378),
        maxWidth: 'calc(100vw - 18px)',
    },
});

export const ProductGallerySticky: StyleRule = () => ({
    position: 'sticky',
    top: pxToRem(16),
    height: 'fit-content',
    // Paddle zones sit over the image: without this, repeated clicks select the surrounding
    // nodes and Chrome paints its blue selection tint across the gallery.
    userSelect: 'none',
});

export const ProductGalleryContainer: StyleRule = ({ theme }) => ({
    position: 'relative',
    marginBottom: pxToRem(10),
    [`& .${carouselNavigationClassName}`]: {
        padding: 0,
    },
    [`& .${carouselNavigationItemClassName}`]: {
        position: 'relative',
        height: pxToRem(PRODUCT_THUMB_SIZE),
        width: pxToRem(PRODUCT_THUMB_SIZE),
        overflow: 'hidden',
        border: 0,
        borderRadius: pxToRem(PRODUCT_GALLERY_MEDIA_RADIUS_PX),
        // Safari: clip filtered blur from ImageSlide thumbs the same way as main tiles.
        clipPath: `inset(0 round ${pxToRem(PRODUCT_GALLERY_MEDIA_RADIUS_PX)})`,

        '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            border: `1px solid var(--thumb-ring-color, transparent)`,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            zIndex: 1,
        },

        '&:hover::after, &:focus::after': {
            borderColor: theme.siteVariables.colors.charcoal[250],
        },

        '& > span': {
            display: 'block',
            width: '100%',
            height: '100%',
        },
    },
});

export const FavouriteButton: StyleRule = ({ theme }) => ({
    [`&.${buttonClassName}`]: {
        position: 'absolute',
        right: pxToRem(15),
        top: pxToRem(12),
        borderRadius: '50%',
        zIndex: 3,
        pointerEvents: 'auto',
        overflow: 'hidden',
        height: pxToRem(42),
        width: pxToRem(42),
        visibility: 'visible',
        backgroundColor: 'rgba(250, 250, 250, 0.7)',
        color: '#343434',

        '& span': {
            width: '100%',
            height: '100%',

            [`> span.${svgIconClassName}`]: {
                fill: '#343434',
                width: pxToRem(18),
                height: pxToRem(18),
            },
        },

        ['&.' + buttonClassName]: {
            '&:active': {
                backgroundColor: theme.siteVariables.colors.white[300],
            },
        },

        [theme.screen.tablet]: {
            position: 'unset',
            top: 'unset',
            right: 'unset',
            height: pxToRem(40),
            width: pxToRem(40),
            visibility: 'visible',

            '& span': {
                [`> span.${svgIconClassName}`]: {
                    fill: '#141414',
                    width: pxToRem(16),
                    height: pxToRem(16),
                },
            },

            '& span:hover': {
                backgroundColor: 'white',
                opacity: 1,
                [`> span.${svgIconClassName}`]: {
                    fill: '#141414',
                },
            },
        },
    },
});

/** Inline variant used in the top-right controls row above the gallery overlay. */
export const FavouriteButtonInline: StyleRule = ({ theme }) => ({
    [`&.${buttonClassName}`]: {
        position: 'relative',
        top: 'auto',
        right: 'auto',
        borderRadius: '50%',
        pointerEvents: 'auto',
        overflow: 'hidden',
        height: pxToRem(40),
        width: pxToRem(40),
        visibility: 'visible',
        backgroundColor: 'white',
        opacity: 0.7,

        '& span': {
            width: '100%',
            height: '100%',
            [`> span.${svgIconClassName}`]: {
                fill: '#141414',
                width: pxToRem(16),
                height: pxToRem(16),
            },
        },

        '&:hover': {
            opacity: 1,
            backgroundColor: 'white',
            [`> span.${svgIconClassName}`]: {
                fill: '#141414',
            },
        },
        '&:active': {
            backgroundColor: theme.siteVariables.colors.white[300],
        },
    },
});

export const PaddleButton: StyleRule = ({ theme }) => ({
    width: pxToRem(32),
    height: pxToRem(32),
    marginTop: pxToRem(1),
    fill: theme.siteVariables.colors.primitive.white,
});

export const GalleryThumbnails: StyleRule = ({ theme }) => ({
    paddingLeft: pxToRem(18),
    '& .swiper': {
        '& .swiper-wrapper': {
            height: pxToRem(90),
            '& .swiper-slide': {
                overflow: 'hidden',
                width: `${pxToRem(92)} !important`,
                border: '2px solid transparent',
                borderBottom: 'none',
                borderTop: 'none',
            },

            '& .swiper-slide-thumb-active': {
                marginLeft: pxToRem(1),
                padding: 0,
                border: `2px solid ${theme.siteVariables.colors.charcoal[250]} !important`,
                width: `${pxToRem(90)} !important`,
                height: pxToRem(90),
            },
        },
    },
});

export const ViewImageOverlay: StyleRule = () => ({
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'initial',
    position: 'absolute',
    cursor: 'none',
    overflow: 'hidden',
});

/** Magnifying glass icon that follows the mouse cursor over the image overlay. */
export const CursorIcon: StyleRule = ({ theme }) => ({
    position: 'absolute',
    pointerEvents: 'none',
    transform: 'translate(-50%, -50%)',
    width: pxToRem(80),
    height: pxToRem(80),
    backgroundColor: hex2rgba(theme.siteVariables.colors.primitive.black, 0.1),
    [`& span .${svgIconClassName}`]: {
        height: pxToRem(22),
        width: pxToRem(22),
        '& svg': {
            fill: theme.siteVariables.colors.primitive.white,
        },
    },
});

export const DownloadButton: StyleRule = () => ({
    pointerEvents: 'auto',
    backgroundColor: 'white',
    opacity: 0.7,
    width: pxToRem(40),
    height: pxToRem(40),
    '&[aria-hidden="true"]': {
        display: 'none',
    },
    '&:hover': {
        opacity: 1,
        backgroundColor: 'white !important',
    },
});

export const VideoButton: StyleRule = ({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    ['&.' + buttonClassName]: {
        borderColor: theme.siteVariables.colors.primitive.white,
        backgroundColor: 'transparent',
        width: pxToRem(112),
        height: pxToRem(112),
        ['& .' + svgIconClassName]: {
            fill: theme.siteVariables.colors.primitive.white,
        },
    },
});
